/**
 * Created by Pankaj on 18-Sep-2019
 */

import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Back, Ease} from "gsap";
import {SoundManager} from "./resource/sound/SoundManager";
import {RomanEmpireGameAssets} from "./RomanEmpireGameAssets";
import {IRomanEmpireLayerConfig} from "./stage/IRomanEmpireLayerConfig";
import {IRomanEmpireSymbolStates} from "./symbols/RomanEmpireReelSymbol";

export class RomanEmpireGameConfig implements IGameConfig {

    public static instance:RomanEmpireGameConfig = new RomanEmpireGameConfig();

    BROWSER_FPS:number = 30;

    GAME_WIDTH:number = 1280;
    GAME_HEIGHT:number = 720;

    DEFAULT_LAYER_SCALE_OPTION:IDeviceDependentConfig<LayerScaleOption> = {
        desktop : {landscape : LayerScaleOption.FIT_WINDOW},
        mobile : {
            landscape : LayerScaleOption.FIT_WINDOW,
            portrait : LayerScaleOption.REELS_FIT_WINDOW
        }
    };

    LAYERS:IRomanEmpireLayerConfig = {
        BACKGROUND : {name : "background"},
        REEL_BACKGROUND : {name : "reelBackground"},
        //BEFORE_SYMBOL: {name: "beforeSymbol"},
        SYMBOLS : {name : "symbols"},
        REEL_ATTENTION : {name : "reelAttention"},
        SYMBOLS_IDLE : {name : "symbolIdleAnimation"},
        SCATTER_ANIMATION : {name : "scatterAnimation"},
        WALKING_WILD : {name : "walkingWild"},
        GINIE_FEATURE : {name : "genieFeature"},
        FOREGROUND : {name : "foreGround"},
        BET_LINES : {name : "betLines"},
        FOUNTAIN : {name : "coinFountain"},
        INITIAL_WIN_AMOUNT_BET_LINES : {name : "initialWInAmountBetLines"}, // #315
        WIN_PRESENTATIONS : {name : "winPresentations"},
        WIN_MULTIPLIER : {name : "winMultiplier"},
        BIG_WIN : {name : "bigWin"},
        WIN_COUNT_UP : {name : "winCountUp"},
        INTRO : {name : "introScreen"},
        FREE_SPIN_INTRO : {name : "freeSpinIntro", scaleOption : LayerScaleOption.FIT_WINDOW},
        FREE_SPIN_OUTRO : {name : "freeSpinOutro", scaleOption : LayerScaleOption.FIT_WINDOW},
        TWEAK_MODULE : {name : "tweakModule", scaleOption : LayerScaleOption.FIT_WINDOW},
    };

    //BET_WIN_MODE: BetWinMode = BetWinMode.BET_LINE;
    BET_WIN_MODE:BetWinMode = BetWinMode.BET_LINE;
    SPIN_MODE:SpinMode = SpinMode.SPIN;

    REEL_AREA_POS_X:number = 0;
    REEL_AREA_POS_Y:number = 90;
    REEL_AREA_WIDTH:number = 720;
    REEL_AREA_HEIGHT:number = 198 * 3;
    REEL_AREA_CENTER_X:number = this.REEL_AREA_POS_X + this.REEL_AREA_WIDTH * 0.5;

    SYMBOL_STATES:IRomanEmpireSymbolStates = {
        normal : {
            prio : 0,
            keyword : "normal",
            texturesCreator : (symName:string, stateKey:string) => {
                return RomanEmpireGameAssets.getSymbolStateTextures(symName);
            }
        },
        spin : {
            prio : 1,
            keyword : "spin",
            texturesCreator : (symName:string, stateKey:string) => {
                return RomanEmpireGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        noWin : {
            prio : 2,
            keyword : "noWin",
            texturesCreator : (symName:string, stateKey:string) => {
                return RomanEmpireGameAssets.getSymbolStateTextures(symName);
            }
        },
        win : {
            prio : 3,
            keyword : "win",
            texturesCreator : (symName:string, stateKey:string) => {
                return RomanEmpireGameAssets.getSymbolStateTextures(symName);
            }
        },
        initialWin : {
            prio : 2,
            keyword : "initialWin",
            texturesCreator : (symName:string, stateKey:string) => {
                return RomanEmpireGameAssets.getSymbolStateTextures(symName, stateKey);
            },
            // offsetGetter: this.OFFSET_GETTER_FOR_OVERLAPPING_SYMBOLS
        },
        hidden : {
            prio : 4,
            keyword : "hidden"
        },
        idle : {
            prio : 5,
            keyword : "idle",
            texturesCreator : (symName:string, stateKey:string) => {
                return RomanEmpireGameAssets.getSymbolStateTextures(symName);
            }
        }
    };
    SYMBOL_WIDTH:number = 144;
    SYMBOL_HEIGHT:number = 198;
    SYMBOL_BOTTOM_PADDING:number = 0;

    REELS_NUM:number = 5;
    SPACE_BETWEEN_REELS:number = 0;
    REEL_DISPLAY_POSITIONS:[number, number][] = [
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0]];
    SYMBOLS_NUM_IN_REEL:[number, number, number, number, number] = [1, 0, 3, 0, 1];
    SYMBOLS_ALL:string[] = ["M1", "M2", "M3", "M4", "M5", "L1", "L2", "L3", "L4", "L5", "S"];
    SYMBOLS_NUM_PER_REEL:number = 3;
    SYMBOLS_WITH_WIN_SPINE:string[] = ["M1", "M2", "M3", "M4", "M5", "L1", "L2", "L3", "L4", "L5",];

    REEL_SPIN_TIME:number = 0.9;
    REEL_FAST_SPIN_SPIN_TIME:number = 0.2;

    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION:number = 0.2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY:number = 0.3;
    REEL_SPIN_START_EASE:Ease = Back.easeIn.config(4);
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION:number = 0.2;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY:number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG:[number, number] = [1.5, 0.95];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG:[number, number] = [1.2, 0.75];

    REEL_SPIN_START_SPEED:number = 0.3;
    REEL_SPIN_SPIN_SPEED:number = 0.04;
    REEL_NEAR_WIN_SPIN_SPEED:number = 0.15;
    REEL_SPIN_STOP_SPEED:number = 0.5;
    REEL_SPIN_QUICK_STOP_SPEED:number = 0.04;

    MIN_STOPPING_TIME:number = 2;
    REEL_STOP_DELAY:number = 0.3;
    REEL_QUICK_STOP_DELAY:number = 0.01;
    REEL_NEAR_WIN_STOP_DELAY:number = 3.0;
    REEL_RESPIN_NEAR_WIN_STOP_DELAY:number[] = [0.5, 1, 1.5, 2, 2.5];


    REEL_SETS:IReelSets = {
        "BASE_REELSET" : [
            ["M5", "L5", "L4", "L1", "L2", "M5", "L1", "M3", "L5", "L2", "L3", "L5", "L4", "M3", "L5", "L1", "L2", "M3", "L5", "L3", "M1", "L2", "M5", "L5", "L1", "L4", "M5", "L5", "L1", "M5", "L2", "L3", "L4", "L2", "L3", "L2", "L1", "M3", "L2", "L3", "M5", "L5", "L3", "L1", "M5", "L5", "M3", "L3", "M5", "L2", "S", "L3", "L5", "L1", "L2", "M3", "L5", "L3", "L2", "L1", "L5", "M5", "L2", "L3", "M3", "L1", "L3", "L2", "M5", "L1", "M2", "M5", "L5", "L4", "L1", "L2", "M5", "L1", "M3", "L5", "L2", "L3", "L5", "L4", "M3", "L5", "L1", "L2", "M3", "L5", "L3", "M1", "L2", "M5", "L5", "L1", "L4", "M5", "L5", "L1", "M5", "L2", "L3", "L4", "L2", "L3", "L2", "L1", "M3", "L2", "L3", "M5", "L5", "L3", "L1", "M5", "L5", "M3", "L3", "M5", "L2", "S", "L3", "L5", "L1", "L2", "M3", "L5", "L3", "L2", "L1", "L5", "M5", "L2", "L3", "M3", "L1", "L3", "L2", "M5", "L1", "M2", "L5", "L2", "M5", "L5", "M4", "L2", "L5", "L3", "L4", "L1", "M3", "L3", "L1", "M3", "L2", "L5", "M5", "S", "L3", "L2", "L4", "L1", "L3", "M4", "L1", "M3", "L2", "L3", "L5", "L2", "L3", "L1", "M5", "M4", "L5", "L2", "M5", "L5", "M1", "L3", "M5", "L2", "L3", "M3", "L2", "L3", "L5", "L2", "L3", "L5", "M3", "L1", "M5", "L3", "M4", "L1", "L3", "M5", "S", "L2", "L5", "M3", "L2", "L5", "M3", "L1", "L3", "L4", "L5", "M3", "L1", "L5", "L2", "M3", "L5", "L1", "L3", "L5", "L4"],
            ["L3", "L4", "M3", "L5", "M4", "L4", "L1", "M3", "L3", "M2", "M4", "L1", "L5", "L4", "L1", "M2", "L5", "L4", "M3", "M5", "L4", "M1", "L3", "L5", "M3", "L4", "L1", "L3", "M4", "L2", "L4", "S", "L1", "L4", "L5", "M3", "M5", "L3", "L2", "L4", "L5", "L1", "L4", "M4", "L2", "L1", "L3", "M5", "L5", "L2", "M2", "M5", "L4", "L5", "M3", "L4", "L5", "L2", "L4", "M5", "L1", "S", "L5", "M5", "L4", "M5", "L3", "L4", "M5", "L1", "L4", "L3", "L5", "L1", "L4", "M4", "L3", "L1", "L5", "M4", "L3", "L2", "M5", "L5", "L2", "L3", "L5", "L3", "L4", "M2", "L1", "L5", "M4", "L3", "S", "L2", "M5", "L5", "L2", "L3", "L5", "L3", "L4", "M2", "L5", "M4", "L4", "L5", "L1", "M4", "L1", "M3", "L3", "S", "L1", "L4", "M4", "L1", "L5", "M2", "L3", "L1", "M5", "M4", "L4", "M1", "L5", "L3", "L2", "L1", "L3", "L4", "M4", "L5", "M4", "M2", "S", "L3", "L1", "M5", "L5", "L3", "L4", "L5", "M4", "L2", "M2", "L5", "L4", "M5", "L2", "L3", "L1", "M2", "L4", "L3", "M3", "L1", "M1", "L4", "L5", "L1", "L4", "L2", "L3", "M5"],
            ["L4", "L3", "M4", "L4", "L1", "M5", "L2", "L4", "M4", "S", "L2", "M5", "L4", "L3", "M4", "M3", "L4", "L1", "L3", "L4", "M2", "M4", "M5", "L5", "M4", "M5", "L4", "L2", "M5", "L5", "M1", "L2", "M5", "L5", "L2", "M5", "L5", "M4", "L4", "L2", "L1", "M4", "L2", "L4", "M3", "L3", "L1", "L4", "M4", "L2", "L4", "M3", "L3", "M5", "L2", "L1", "L3", "M2", "L5", "M4", "M5", "L1", "L4", "M3", "L5", "L2", "L1", "S", "L4", "L2", "L3", "M5", "M3", "L2", "L4", "L1", "L5", "M1", "L3", "L4", "L1", "L5", "M4", "L3", "L5", "L4", "S", "L3", "L5", "M4", "L2", "L5", "M3", "L2", "M4", "L3", "L4", "L5", "L2", "L4", "L2", "M4", "M5", "L2", "L3", "S", "L5", "M3", "L4", "L2", "L1", "L5", "L4", "L1", "M3", "M1", "L2", "M5", "M4", "L5", "M4", "L1", "L4", "L5", "S", "L4", "L5", "L2", "L4", "M4", "L5", "L4", "L3", "M2", "L5", "M4", "L2", "L5", "L1", "M5", "M4", "L1", "L5", "M4", "M5", "L1", "L4", "M3", "L5", "L2", "L1", "S", "L4", "L2", "L3", "M5", "M3", "L2", "L4", "L1", "L5", "M1", "L3", "L4", "L1", "L5", "M4", "L3", "L5", "L4", "S", "L3", "L5", "M4", "L2", "L5", "M3", "L2", "M4", "L3", "L4", "L5", "L2", "L4", "M2", "L2", "M4", "M5", "L2", "L3", "S", "L5", "M3", "L4", "L2", "L1", "L5", "L4", "L1", "M3", "M1", "L2", "M5", "M4", "L5", "M4", "L1", "L4", "L5", "S", "L4", "L5", "L2", "L4", "M4", "L5", "L4", "L3", "M2", "L5", "M4", "L2", "L5", "L1", "M5", "M4", "L1"],
            ["L2", "L5", "L3", "M5", "M4", "L3", "M3", "L5", "L2", "M3", "S", "L5", "L3", "M3", "L2", "L1", "L5", "M4", "L3", "L1", "M1", "M4", "L2", "M5", "L3", "L1", "M4", "M5", "L5", "L3", "L2", "M5", "L5", "L3", "M5", "L2", "L4", "S", "L5", "L2", "L3", "M5", "L5", "L3", "L2", "L3", "M2", "L5", "L1", "L3", "L4", "L2", "M3", "L5", "M4", "M1", "L3", "M3", "M5", "L5", "M5", "L2", "L1", "L5", "M4", "S", "L3", "M3", "L2", "L3", "M3", "M4", "L2", "M5", "L1", "L2", "M3", "L3", "M5", "M4", "L2", "L5", "M5", "L2", "M2", "S", "L3", "L4", "L5", "M5", "L1", "L3", "M4", "L3", "S", "M5", "M3", "L3", "L5", "L2", "M5", "L2", "L1", "L3", "M5", "L5", "L2", "M4", "L1", "L2", "L3", "M3", "M1", "L2", "L5", "L3", "L2", "L1", "L4", "L3", "L2", "L5", "L1", "M2", "M3", "L5", "M4", "L4", "L2", "L3", "L1", "M4", "L3", "M5", "S", "L5", "M4", "L2", "M3", "L4", "M5", "L2", "L5", "M5"],
            ["S", "M3", "L3", "L4", "L2", "L5", "L1", "M5", "L3", "L4", "L1", "M4", "L3", "M4", "L4", "L5", "L2", "L3", "L4", "M4", "M1", "M5", "L4", "L3", "L2", "L5", "L4", "L1", "M4", "L3", "M5", "L5", "M3", "L1", "L5", "M5", "L5", "L3", "L1", "S", "L2", "M4", "L3", "L5", "L4", "L3", "M5", "L4", "M2", "M4", "L5", "L1", "L4", "M5", "L5", "M4", "M3", "L4", "L3", "S", "M4", "L5", "L3", "L2", "L4", "L3", "L1", "L5", "L3", "L2", "M3", "L1", "L5", "L3", "L2", "L5", "L3", "L5", "S", "M3", "L3", "L4", "L2", "L5", "L1", "M5", "L3", "L4", "L1", "M1", "M4", "L3", "M4", "L4", "L5", "L2", "L3", "L4", "M4", "M1", "M5", "L4", "L3", "L2", "L5", "L4", "L1", "M4", "L3", "M5", "L5", "M3", "L1", "L5", "M5", "L5", "L3", "L1", "S", "L2", "M4", "L3", "L5", "L4", "L3", "M5", "L4", "M2", "M4", "L5", "L1", "L4", "M5", "L5", "M4", "M3", "L4", "L3", "S", "M4", "L5", "L3", "L2", "L4", "L3", "L1", "L5", "L3"]
        ],
        "RESPIN_REELSET" : [
            ["L1", "L4", "L2", "L1", "L5", "L2", "L4", "L1", "M5", "L4", "M5", "L2", "L5", "L4", "M3", "M5", "L3", "L4", "S", "M5", "L4", "L3", "M5", "L5", "L4", "L1", "L5", "M1", "M4", "L1", "M5", "M3", "L4", "L3", "L2", "L5", "M4", "M5", "L3", "L1", "L2", "L5", "L3", "L2", "L4", "L5", "M2", "M4", "L1", "L4", "M3", "L1", "L4", "L3", "L1", "L5", "L3", "L4", "L5", "M5", "L3", "L4", "M5", "L4", "L2", "S", "M5", "L1", "L4", "M5", "L1", "L4", "L3", "L5", "L2", "L4", "L1", "L5", "L3", "M4", "M3", "L3", "L4", "M4", "L4", "L2", "M5", "L4", "L3", "L1", "L4", "M4", "L1", "L5", "M3", "L4", "M5", "L5", "M3", "L4", "L2", "M5", "L5", "M4", "L1", "L2", "M5", "M3", "L1", "M4", "M5", "L3", "L4", "L2", "L4", "M3", "L4", "M3", "L3", "L2", "S", "L4", "M4", "L3", "M5", "L4", "L5", "M3", "L4", "M5", "L4", "L1", "M5", "L5", "L3", "L2", "L4", "L1", "M5"],
            ["L3", "M3", "L4", "L3", "L5", "L3", "M4", "L3", "L4", "M4", "L3", "M3", "L5", "L3", "L4", "L1", "L3", "M4", "L2", "L3", "L4", "M4", "L5", "L4", "L3", "M3", "L2", "L3", "M4", "L5", "L3", "M3", "L5", "L3", "M4", "M3", "L3", "L4", "M5", "L2", "L3", "L1", "M5", "L3", "L4", "L5", "M4", "L3", "M3", "L5", "M4", "M5", "L2", "L3", "M5", "L4", "L3", "M3", "L4", "L3", "L2", "L5", "M4", "M3", "L3", "L1", "L2", "M3", "L3", "M4", "L4", "M3", "L2", "L1", "L5", "L2", "S", "L4", "L3", "M3", "L2", "L3", "M3", "L1", "L3", "L2", "L5", "L4", "M3", "L3", "L5", "M3", "L4", "L3", "L5", "M3", "M2", "S", "L2", "L1", "M3", "L4", "L2", "L1", "L3", "L5", "L2", "M5", "M3", "L3", "L5", "L4", "L3", "L5", "M4", "L2", "L3", "M3", "M1", "M4", "L5", "L3", "M4", "M3", "L3", "L4", "M3", "L3", "M4", "L5", "L2", "L4", "L3", "M3", "L2", "L3", "M4", "M3", "L3", "L4", "M3", "L3", "M4", "L4", "L2", "M3", "M4"],
            ["L5", "M3", "L4", "M4", "L3", "M3", "L5", "L3", "M4", "M3", "L5", "L3", "L4", "M4", "L5", "L4", "M4", "L2", "L3", "M1", "L5", "L2", "L3", "L5", "M3", "M5", "L3", "L5", "M4", "L3", "S", "L5", "L4", "L3", "L5", "L3", "L1", "M3", "L3", "L5", "L3", "M4", "L5", "L3", "L2", "L5", "M4", "L3", "L2", "M5", "M3", "L2", "L5", "M4", "L3", "L2", "M5", "L3", "L1", "M4", "L5", "L3", "M4", "L2", "L5", "L3", "L4", "M2", "L5", "M5", "L2", "L3", "L4", "M3", "M4", "L3", "L5", "M3", "L3", "L5", "L2", "M3", "L5", "L2", "M3", "M5", "L5", "S", "M4", "L4", "L5", "M4", "M3", "L3", "L5", "M4", "L3", "M3", "M4", "L3", "L2", "M4", "L3", "L4", "M4", "M3", "M2", "L3", "L2", "L5", "M5", "L3", "L5", "M3", "L3", "L5", "M4", "M3", "L3", "L4", "L5", "L3", "M4", "L5", "M4", "L3", "M1", "S", "L5", "M4", "L3", "M3", "M4", "L3", "M5", "L5", "L3", "M3", "L5", "L1", "L3", "M3", "M4", "L5", "L4", "L3", "M3"],
            ["L4", "L3", "L2", "L4", "L2", "L5", "L3", "L2", "L4", "L2", "L4", "L3", "L2", "L4", "L2", "M1", "L1", "L2", "L1", "L4", "M4", "L2", "M5", "L3", "L2", "L4", "L5", "M4", "L3", "M5", "L2", "L1", "S", "M5", "L4", "L1", "L3", "L2", "L4", "M4", "L5", "L4", "M5", "M3", "L2", "L4", "L1", "L2", "L5", "L4", "L1", "L4", "L1", "M5", "L3", "L4", "L2", "M5", "L1", "L2", "L3", "L1", "M3", "L4", "M5", "L2", "L4", "M4", "L1", "L2", "L4", "L2", "L4", "L2", "L3", "M5", "L4", "L2", "L4", "M5", "L2", "M3", "M2", "M5", "L2", "L3", "L4", "L1", "L3", "M5", "L2", "L3", "M5", "L1", "L4", "L3", "L2", "L4", "L3", "S", "L2", "L4", "L1", "L4", "L2", "M5", "M4", "L3", "L4", "L2", "L3", "M4", "L4", "M5", "L2", "M3", "L1", "M5", "L2", "L3", "L4", "L2", "L5", "L3", "L4"],
            ["L5", "L2", "M3", "L4", "L1", "L2", "L5", "M3", "L2", "L3", "L2", "L5", "L3", "L2", "L4", "L5", "L2", "M5", "L3", "L5", "L4", "M5", "L2", "L5", "L3", "L4", "L2", "L5", "M3", "M1", "L2", "L1", "L5", "L4", "M5", "L5", "L4", "L2", "L3", "L1", "S", "L2", "M5", "L5", "L2", "L4", "L3", "L2", "L4", "M3", "M5", "L1", "L3", "M3", "L4", "L5", "L1", "L2", "L4", "L5", "L2", "L4", "M5", "L5", "L2", "L3", "M5", "L5", "L4", "L3", "M3", "M4", "L3", "L5", "L3", "M5", "L5", "L3", "L1", "M3", "L4", "L2", "L5", "L3", "L2", "L4", "L1", "L5", "L2", "S", "M5", "L5", "L1", "L4", "M3", "L2", "L5", "L1", "M4", "L5", "L4", "L3", "L2", "L5", "L3", "L2", "M5", "L3", "L4", "M2", "L3", "L5", "L2", "M3", "L1", "L2", "L5", "L4", "L2", "M3", "L4", "L3", "M5", "L5", "L3", "L4", "M3", "L4", "L3", "M5", "L2", "L3", "L1", "L5", "L2", "M5", "L5", "M3", "L3", "L2", "L4", "L3", "L5", "L2", "L5", "M4", "M5", "L3"]
        ]
    };

    WIN_RATIOS:IWinRatios = {
        normalWin : [
            {
                ratio : 0,
                text : "Tiny Win",
                duration : 0,
                type : WinRatioType.TINY
            },
            {
                ratio : 1.5,
                text : "Small Win",
                duration : 0,
                type : WinRatioType.SMALL
            },
            {
                ratio : 3,
                text : "Medium Win",
                duration : 0.637,
                type : WinRatioType.MEDIUM
            },
            {
                ratio : 5,
                text : "Large Win",
                duration : 1.117,
                type : WinRatioType.LARGE
            }
        ],
        bigWin : [
            {
                ratio : 15,
                text : "Big Win",
                duration : 7.802,
                type : WinRatioType.BIG_WIN
            },
            {
                ratio : 25,
                text : "Mega Win",
                duration : 7.802,
                type : WinRatioType.MEGA_WIN

            },
            {
                ratio : 50,
                text : "Super Mega Win",
                duration : 7.802,
                type : WinRatioType.SUPER_MEGA_WIN
            }
        ]
    };

    // GAME_TWEAKER_CONFIG: IGameTweakerConfig = {
    //     layer: this.LAYERS.TWEAK_MODULE.name,
    //     baseWindowWidth: 400,
    //     moduleHeight: 32,
    //     copyAllButtonText: "Copy All",
    //     buttonSkinIdle: "button",
    //     buttonSkinOver: "button",
    //     buttonSkinDown: "button",
    //     buttonSkinDisabled: "button-disabled"
    // };
    PERFORMANCE_LOGGER:boolean = true;

    private constructor() {
        if(RomanEmpireGameConfig.instance) {
            throw new Error("RomanEmpireGameConfig.constructor() - Instantiation failed: Singleton.");
        }
        const soundManager:SoundManager = new SoundManager();
    }

    GENIE_TIMES_BET_COIN_WIN_ADD_DURATION:number = 0.8;
    TOTAL_BET_LINES:number = 20;
    // #307
    WIN_BELOW_STAKE: boolean = false;
}